import { FormEvent, useState } from "react"
import { useNavigate } from "react-router-dom"
import { ChevronLeft } from "lucide-react"
import {
  attachAssessmentPortrait,
  createAssessmentSession,
} from "~/api/assessmentSessions"
import { PhotoUploadPanel } from "~/components/PhotoUploadPanel"
import { SearchableBreedSelector } from "~/components/SearchableBreedSelector"
import { LoggedInScreenCard } from "~/components/LoggedInScreenCard"
import { PageShell } from "~/components/PageShell"
import { DOG_AGE_RANGE_OPTIONS, type DogAgeRange } from "~/data/dogAgeRanges"

type DogSex = "male" | "female"

const ADD_DOG_SESSION_KEY = "doggy_dynamo.add_dog_session"

export function persistAddDogSession(state: {
  sessionToken: string
  dogName: string
  dogSex: DogSex
  dogAgeRange: DogAgeRange
  dogBreed?: string
  dogMixDetail?: string
  photoAttached: boolean
}) {
  sessionStorage.setItem(ADD_DOG_SESSION_KEY, JSON.stringify(state))
}

export function AddDogPage() {
  const navigate = useNavigate()
  const [dogName, setDogName] = useState("")
  const [dogAgeRange, setDogAgeRange] = useState<DogAgeRange | "">("")
  const [dogBreed, setDogBreed] = useState("")
  const [dogMixDetail, setDogMixDetail] = useState("")
  const [dogSex, setDogSex] = useState<DogSex | null>(null)
  const [photo, setPhoto] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const trimmed = dogName.trim()
  const canContinue = Boolean(trimmed && dogAgeRange && dogSex && photo)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (!canContinue || !dogSex || !dogAgeRange || !photo) return
    setSubmitting(true)
    setError(null)
    try {
      const session = await createAssessmentSession(
        trimmed,
        dogSex,
        dogAgeRange,
        dogBreed || undefined,
        dogMixDetail || undefined,
      )
      await attachAssessmentPortrait(session.session_token, photo)
      const navigationState = {
        sessionToken: session.session_token,
        dogName: trimmed,
        dogSex,
        dogAgeRange,
        dogBreed: dogBreed || undefined,
        dogMixDetail: dogMixDetail || undefined,
        skipName: true as const,
        photoAttached: true,
      }
      persistAddDogSession(navigationState)
      navigate(`/assessment?add_dog=1`, { state: navigationState })
    } catch (err) {
      const message =
        err instanceof Error && err.message.trim()
          ? err.message
          : "Could not start add-dog flow. Please try again."
      setError(message)
      setSubmitting(false)
    }
  }

  return (
    <PageShell innerClassName="max-w-lg w-full" fixedTabNav>
      <LoggedInScreenCard>
      <header className="mb-4 flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Back"
            className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
          >
            <ChevronLeft size={20} aria-hidden />
          </button>
          <h1 className="flex-1 text-lg font-semibold text-[var(--color-text-primary)]">
            Add Dog to Pack
          </h1>
      </header>

      <div>
        <form
          onSubmit={handleSubmit}
          className="space-y-5 rounded-xl border border-[var(--color-divider)] bg-[var(--color-surface-card)] p-6"
        >
          <div>
            <h2 className="text-2xl font-semibold text-[var(--color-text-primary)]">
              Let&apos;s meet your next dog!
            </h2>
            <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
              We&apos;ll create a unique personality profile for your new pack member.
            </p>
          </div>

          <label className="block text-sm font-medium text-[var(--color-text-primary)]">
            Name
            <input
              type="text"
              value={dogName}
              onChange={(e) => setDogName(e.target.value)}
              className="mt-1 w-full rounded-lg border border-[var(--color-divider)] bg-[var(--color-bg-primary)] px-4 py-3"
              required
            />
          </label>

          <label className="block text-sm font-medium text-[var(--color-text-primary)]">
            Age range
            <select
              value={dogAgeRange}
              onChange={(e) => setDogAgeRange(e.target.value as DogAgeRange | "")}
              className="mt-1 w-full rounded-lg border border-[var(--color-divider)] bg-[var(--color-bg-primary)] px-4 py-3"
              required
            >
              <option value="" disabled>
                Select age range
              </option>
              {DOG_AGE_RANGE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <SearchableBreedSelector
            value={dogBreed}
            mixDetail={dogMixDetail}
            onChange={(breed, mixDetail) => {
              setDogBreed(breed)
              setDogMixDetail(mixDetail ?? "")
            }}
            placeholder="Search or select breed (optional)"
          />

          <div>
            <p className="text-sm font-medium text-[var(--color-text-primary)]">Sex</p>
            <div className="mt-2 flex gap-3">
              {(["male", "female"] as const).map((sex) => (
                <button
                  key={sex}
                  type="button"
                  onClick={() => setDogSex(sex)}
                  aria-pressed={dogSex === sex}
                  className={`min-w-28 rounded-lg border px-4 py-2 text-sm font-medium capitalize ${
                    dogSex === sex
                      ? "border-[var(--color-text-primary)] bg-[var(--color-text-primary)] text-[var(--color-surface-card)]"
                      : "border-[var(--color-divider)]"
                  }`}
                >
                  {sex}
                </button>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">
              Make this picture yours
            </h3>
            <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
              Upload a photo of your dog and we&apos;ll create a personalised hero portrait. Included
              free with every account.
            </p>
            <div className="mt-3">
              <PhotoUploadPanel
                dogName={trimmed || "your dog"}
                photo={photo}
                previewUrl={previewUrl}
                error={error}
                required
                onPhotoSelected={(file, preview) => {
                  setError(null)
                  setPhoto(file)
                  setPreviewUrl(preview)
                }}
                onClear={() => {
                  setError(null)
                  setPhoto(null)
                  setPreviewUrl(null)
                }}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={!canContinue || submitting}
            className={`w-full rounded-lg px-6 py-3 font-semibold text-white transition ${
              canContinue && !submitting
                ? "bg-[var(--color-brand-primary)] hover:opacity-90"
                : "cursor-not-allowed bg-[var(--color-brand-primary)]/40"
            }`}
          >
            {submitting ? "Starting…" : "Begin Assessment"}
          </button>
        </form>
      </div>
      </LoggedInScreenCard>
    </PageShell>
  )
}
